import React, { useState, useEffect } from 'react';
import { getDashboardData } from '../utils/api';

function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    totalClients: 0,
    activeCampaigns: 0,
    emailsSentToday: 0,
    remainingQuota: 0,
    recentEmails: [],
    scheduledEmails: []
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getDashboardData();
        setDashboardData(data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const refreshDashboard = () => {
    setLoading(true);
    getDashboardData()
      .then(data => {
        setDashboardData(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error refreshing dashboard:', error);
        setLoading(false);
      });
  };

  // Helper function to get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'Sent':
        return 'success';
      case 'Pending':
        return 'warning';
      case 'Failed':
        return 'danger';
      case 'Opened':
        return 'info';
      case 'Clicked':
        return 'primary';
      case 'Replied':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  if (loading) {
    return <div className="loader"></div>;
  }

  return (
    <div id="dashboard">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Dashboard</h1>
        <div>
          <button className="btn btn-primary" onClick={refreshDashboard}>
            <i className="fas fa-sync-alt"></i> Refresh
          </button>
        </div>
      </div>
      
      <div className="row">
        <div className="col-md-3">
          <div className="card stats-card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-subtitle mb-2 text-muted">Total Clients</h6>
                  <h2 className="card-title mb-0">{dashboardData.totalClients}</h2>
                </div>
                <div className="stats-icon">
                  <i className="fas fa-users"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card stats-card success">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-subtitle mb-2 text-muted">Active Campaigns</h6>
                  <h2 className="card-title mb-0">{dashboardData.activeCampaigns}</h2>
                </div>
                <div className="stats-icon">
                  <i className="fas fa-bullhorn"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card stats-card warning">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-subtitle mb-2 text-muted">Emails Sent Today</h6>
                  <h2 className="card-title mb-0">{dashboardData.emailsSentToday}</h2>
                </div>
                <div className="stats-icon">
                  <i className="fas fa-envelope"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card stats-card danger">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-subtitle mb-2 text-muted">Remaining Quota</h6>
                  <h2 className="card-title mb-0">{dashboardData.remainingQuota}</h2>
                </div>
                <div className="stats-icon">
                  <i className="fas fa-chart-pie"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="row mt-4">
        <div className="col-md-6">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <span>Recent Emails</span>
              <a href="#" className="text-primary" onClick={(e) => {
                e.preventDefault();
                // You would add code here to switch to analytics section
              }}>View All</a>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>Subject</th>
                      <th>Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.recentEmails && dashboardData.recentEmails.length > 0 ? (
                      dashboardData.recentEmails.map((email, index) => (
                        <tr key={index}>
                          <td>{email.clientName}</td>
                          <td>{email.subject}</td>
                          <td>{email.date}</td>
                          <td>
                            <span className={`badge bg-${getStatusBadgeColor(email.status)}`}>
                              {email.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center">No recent emails</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <span>Scheduled Emails</span>
              <a href="#" className="text-primary" onClick={(e) => {
                e.preventDefault();
                // You would add code here to switch to campaigns section
              }}>View All</a>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>Campaign</th>
                      <th>Scheduled Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.scheduledEmails && dashboardData.scheduledEmails.length > 0 ? (
                      dashboardData.scheduledEmails.map((email, index) => (
                        <tr key={index}>
                          <td>{email.clientName}</td>
                          <td>{email.campaignName}</td>
                          <td>{email.scheduledDate}</td>
                          <td>
                            <span className={`badge bg-${getStatusBadgeColor(email.status)}`}>
                              {email.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center">No scheduled emails</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;